import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { ReactNode, useCallback, useState } from 'react';

import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { BillingLine } from '~hooks/useBillingLines/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { formatRateWithType } from '~utils/rateFormatting';

import { formatChargeRate } from '../chargeRateFormatting';
import { getBillingLinesParties } from '../chargeSelection';
import {
  CHARGES_CELL_SX,
  CHARGES_GRID_TEMPLATE_COLUMNS,
  CHARGES_NESTED_HEADER_CELL_SX,
  chargesGridColumn,
  chargesNestedHeaderRowSx,
  chargesNestedHeaderTextSx,
  NO_VALUE_PLACEHOLDER,
  rejectedAmountSx,
} from '../chargesGrid';
import {
  areLinesOnApprovedSettlements,
  ChargeRow,
  getLineSettlementNumber,
} from '../groupBillingLines';
import BillingLineActionsMenu from './BillingLineActionsMenu';
import ChargeLinesCheckbox from './ChargeLinesCheckbox';
import RejectedChip from './RejectedChip';
import SettlementNumberChip from './SettlementNumberChip';
import WasText from './WasText';

const rowSx = {
  alignItems: 'start',
  display: 'grid',
  gridTemplateColumns: CHARGES_GRID_TEMPLATE_COLUMNS,
} as const;

const ROW_CELL_SX = { ...CHARGES_CELL_SX, py: 0.5 } as const;

const ROLLED_UP_TOGGLE_WIDTH_IN_PX = 20;

const rowDividerSx = (theme: Theme) => ({
  borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
});

interface ChargeRowsTableProps {
  nameHeading: string;
  rows: ChargeRow[];
  totals?: { payable: number; receivable: number; label: string };
  totalsActions?: ReactNode;
}

export default function ChargeRowsTable({
  nameHeading,
  rows,
  totals,
  totalsActions,
}: ChargeRowsTableProps) {
  const { currencyFormatter } = useCompanyCurrency();

  const hasRows = rows.length > 0;

  return (
    <Box>
      {hasRows && (
        <Box sx={chargesNestedHeaderRowSx}>
          {[
            [chargesGridColumn.addOnSide, t('approvals.approve_charges.columns.side')],
            [chargesGridColumn.addOnName, nameHeading],
            [chargesGridColumn.addOnRate, t('common.rate')],
            [chargesGridColumn.addOnQuantity, t('approvals.approve_charges.columns.qty')],
            [
              chargesGridColumn.payableAmount,
              t('approvals.approve_charges.columns.pay_total'),
            ],
            [
              chargesGridColumn.receivableAmount,
              t('approvals.approve_charges.columns.rec_total'),
            ],
            [
              chargesGridColumn.payableNumber,
              t('approvals.approve_charges.columns.pay_number'),
            ],
            [
              chargesGridColumn.invoiceNumber,
              t('approvals.approve_charges.columns.invoice_number'),
            ],
          ].map(([column, title]) => (
            <Box
              key={column}
              sx={{ ...CHARGES_NESTED_HEADER_CELL_SX, gridColumn: column }}
            >
              <OverflowAwareText sx={chargesNestedHeaderTextSx}>
                {title}
              </OverflowAwareText>
            </Box>
          ))}
        </Box>
      )}

      {rows.map((row, index) => (
        <ChargeRowItem key={row.key} hasDivider={index > 0} row={row} />
      ))}

      {totalsActions && (
        <Box sx={(theme) => ({ ...rowSx, ...(hasRows ? rowDividerSx(theme) : {}) })}>
          <Box
            sx={{
              ...ROW_CELL_SX,
              display: 'flex',
              gap: 1,
              gridColumn: chargesGridColumn.addOnActions,
              px: 1.5,
              py: 1.5,
            }}
          >
            {totalsActions}
          </Box>

          {hasRows && totals && (
            <>
              <Box
                sx={{ ...ROW_CELL_SX, gridColumn: chargesGridColumn.addOnTotalsLabel }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {totals.label}
                </Typography>
              </Box>
              {[
                {
                  column: chargesGridColumn.payableAmount,
                  side: BillingLineSide.PAYABLE,
                  total: totals.payable,
                },
                {
                  column: chargesGridColumn.receivableAmount,
                  side: BillingLineSide.RECEIVABLE,
                  total: totals.receivable,
                },
              ].map(({ column, side, total }) => (
                <Box key={side} sx={{ ...ROW_CELL_SX, gridColumn: column }}>
                  <OverflowAwareText>
                    <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                      {rows.some((row) => row.side === side)
                        ? currencyFormatter(total)
                        : NO_VALUE_PLACEHOLDER}
                    </Typography>
                  </OverflowAwareText>
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

const sideLabelKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.side.payable',
  [BillingLineSide.RECEIVABLE]: 'approvals.approve_charges.side.receivable',
} satisfies Record<BillingLineSide, string>;

interface ChargeRowItemProps {
  hasDivider: boolean;
  row: ChargeRow;
}

function ChargeRowItem({ hasDivider, row }: ChargeRowItemProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const toggleIsOpen = useCallback(() => setIsOpen((isOpenNow) => !isOpenNow), []);

  const isPayable = row.side === BillingLineSide.PAYABLE;
  const amountColumn = isPayable
    ? chargesGridColumn.payableAmount
    : chargesGridColumn.receivableAmount;
  const numberColumn = isPayable
    ? chargesGridColumn.payableNumber
    : chargesGridColumn.invoiceNumber;

  return (
    <>
      <Box
        data-test-id="charge-row"
        sx={(theme) => ({
          ...rowSx,
          ...(hasDivider ? rowDividerSx(theme) : {}),
        })}
      >
        <Box sx={{ ...ROW_CELL_SX, gridColumn: chargesGridColumn.select }}>
          {!row.isRolledUp && <ChargeLinesCheckbox label={row.name} lines={row.lines} />}
        </Box>

        <Box sx={{ ...ROW_CELL_SX, gridColumn: chargesGridColumn.addOnSide }}>
          <SideChip lines={row.lines} side={row.side} />
        </Box>

        <Box
          sx={{
            ...ROW_CELL_SX,
            alignItems: 'center',
            display: 'flex',
            gap: 0.5,
            gridColumn: chargesGridColumn.addOnName,
          }}
        >
          {row.isRolledUp ? (
            <IconButton
              aria-expanded={isOpen}
              aria-label={t('approvals.approve_charges.toggle_lines', {
                name: row.name,
              })}
              onClick={toggleIsOpen}
              sx={{ borderRadius: 0, color: 'inherit', p: 0 }}
            >
              {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ flexShrink: 0, width: ROLLED_UP_TOGGLE_WIDTH_IN_PX }} />
          )}
          <OverflowAwareText sx={{ fontWeight: 600 }} title={row.name}>
            {row.name}
          </OverflowAwareText>
        </Box>

        <Box sx={{ ...ROW_CELL_SX, gridColumn: chargesGridColumn.addOnRate }}>
          <OverflowAwareText>
            {row.rate === null || row.isRejected
              ? NO_VALUE_PLACEHOLDER
              : formatChargeRate(row, currencyFormatter)}
          </OverflowAwareText>
          {row.wasRate !== null && (
            <WasText
              amount={formatRateWithType(row.wasRate, row.rateType, currencyFormatter)}
            />
          )}
        </Box>

        <Box
          sx={{
            ...ROW_CELL_SX,
            gridColumn: chargesGridColumn.addOnQuantity,
          }}
        >
          <OverflowAwareText>
            {row.isRolledUp
              ? t('approvals.approve_charges.line_count', {
                  count: row.lines.length,
                })
              : (row.quantity ?? NO_VALUE_PLACEHOLDER)}
          </OverflowAwareText>
        </Box>

        <Box sx={{ ...ROW_CELL_SX, gridColumn: amountColumn }}>
          <OverflowAwareText sx={row.isRejected ? rejectedAmountSx : { fontWeight: 700 }}>
            {currencyFormatter(row.total)}
          </OverflowAwareText>
          {!row.isRejected && row.wasTotalAmount !== null && (
            <WasText amount={currencyFormatter(row.wasTotalAmount)} />
          )}
          {row.isRejected && <RejectedChip reason={row.rejectedReason} />}
        </Box>

        <SettlementNumberChip
          hasUnrejectedLines={!row.isRejected}
          hasUnsettledLines={row.hasUnsettledLines}
          isApproved={row.isSettlementApproved}
          settlementNumbers={row.settlementNumbers}
          side={row.side}
          sx={{ gridColumn: numberColumn, py: 0.5 }}
        />

        <Box sx={{ ...ROW_CELL_SX, gridColumn: chargesGridColumn.status }}>
          {!row.isRolledUp && <BillingLineActionsMenu lines={row.lines} />}
        </Box>
      </Box>

      {isOpen &&
        row.lines.map((line) => (
          <RolledUpLine
            key={`${row.key}:${line.id}`}
            amountColumn={amountColumn}
            line={line}
            numberColumn={numberColumn}
          />
        ))}
    </>
  );
}

interface SideChipProps {
  lines: BillingLine[];
  side: BillingLineSide;
}

function SideChip({ lines, side }: SideChipProps) {
  const parties = getBillingLinesParties(lines);

  return (
    <Tooltip title={parties.length === 1 ? parties[0].name : ''}>
      <Typography
        component="span"
        sx={(theme) => ({
          border: `solid 1px ${theme.brandV2.colors.treadGray5}`,
          borderRadius: 0.5,
          color: theme.brandV2.colors.treadBlack,
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.05em',
          px: 0.75,
          py: 0.25,
        })}
      >
        {t(sideLabelKeys[side])}
      </Typography>
    </Tooltip>
  );
}

interface RolledUpLineProps {
  amountColumn: string;
  line: BillingLine;
  numberColumn: string;
}

function RolledUpLine({ amountColumn, line, numberColumn }: RolledUpLineProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const settlementNumber = getLineSettlementNumber(line);

  return (
    <Box
      data-test-id="charge-row-line"
      sx={(theme) => ({
        ...rowSx,
        ...rowDividerSx(theme),
      })}
    >
      <Box
        sx={{
          ...ROW_CELL_SX,
          gridColumn: chargesGridColumn.addOnName,
          pl: 4,
        }}
      >
        <OverflowAwareText sx={(theme) => ({ color: theme.brandV2.colors.treadGray3 })}>
          {line.scope.load?.ticket_number ?? NO_VALUE_PLACEHOLDER}
        </OverflowAwareText>
      </Box>

      <Box sx={{ ...ROW_CELL_SX, gridColumn: chargesGridColumn.addOnRate }}>
        <OverflowAwareText>
          {line.rate === null
            ? NO_VALUE_PLACEHOLDER
            : formatChargeRate(line, currencyFormatter)}
        </OverflowAwareText>
        {line.wasRate !== null && (
          <WasText
            amount={formatRateWithType(line.wasRate, line.rateType, currencyFormatter)}
          />
        )}
      </Box>

      <Box sx={{ ...ROW_CELL_SX, gridColumn: amountColumn }}>
        <OverflowAwareText sx={line.isRejected ? rejectedAmountSx : undefined}>
          {currencyFormatter(line.totalAmount)}
        </OverflowAwareText>
        {line.isRejected && <RejectedChip reason={line.rejectedReason} />}
        {!line.isRejected && line.wasTotalAmount !== null && (
          <WasText amount={currencyFormatter(line.wasTotalAmount)} />
        )}
      </Box>

      <SettlementNumberChip
        hasUnrejectedLines={!line.isRejected}
        isApproved={areLinesOnApprovedSettlements([line])}
        settlementNumbers={settlementNumber && !line.isRejected ? [settlementNumber] : []}
        side={line.side}
        sx={{ gridColumn: numberColumn, py: 0.5 }}
      />

      <Box sx={{ ...ROW_CELL_SX, gridColumn: chargesGridColumn.status }}>
        <BillingLineActionsMenu lines={[line]} />
      </Box>
    </Box>
  );
}
